'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import {
  Eye, EyeOff, LogIn, Sprout, FlaskConical, Shield, ArrowLeft, Mail
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
}

export default function LoginPageClient() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailError, setResetEmailError] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const validate = () => {
    let valid = true;
    setEmailError('');
    setPasswordError('');
    if (!email.trim()) { setEmailError('Username or email is required'); valid = false; }
    if (!password) { setPasswordError('Password is required'); valid = false; }
    else if (password.length < 6) { setPasswordError('Password must be at least 6 characters'); valid = false; }
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      const supabase = createClient();
      const loginEmail = email.trim().includes('@') ? email.trim() : `${email.trim()}@jfagro.my`;
      const { data, error } = await supabase.auth.signInWithPassword({ email: loginEmail, password });
      if (error) {
        toast.error('Invalid credentials. Please check your email and password.');
        setIsLoading(false);
        return;
      }
      // Fetch role from user_profiles
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('farm_role, app_role, full_name')
        .eq('id', data.user.id)
        .maybeSingle();

      const role = profile?.farm_role || profile?.app_role || 'worker';
      const name = profile?.full_name || data.user.email || 'User';
      toast.success(`Welcome back, ${name.split(' ')[0]}!`);

      if (role === 'admin' || role === 'farm-admin') {
        router.push('/admin-operations-suite');
      } else {
        router.push('/worker-field-station');
      }
    } catch {
      toast.error('Sign in failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetEmailError('');
    if (!resetEmail.trim()) {
      setResetEmailError('Email address is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail.trim())) {
      setResetEmailError('Please enter a valid email address');
      return;
    }
    setIsResetting(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail.trim(), {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/reset-password`,
      });
      if (error) {
        toast.error(error.message || 'Failed to send reset email. Please try again.');
      } else {
        setResetSent(true);
        toast.success('Password reset email sent!');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
    setResetEmail('');
    setResetEmailError('');
    setResetSent(false);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background">
      {/* Left Panel — Brand */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] gradient-emerald flex-col justify-between p-10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/5" />
        <div className="absolute bottom-10 -left-16 w-64 h-64 rounded-full bg-white/5" />
        <div className="absolute top-1/2 right-8 w-40 h-40 rounded-full bg-white/5" />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <AppLogo size={176} />
            <span className="text-white font-bold text-2xl tracking-tight">VNL@JFAgro</span>
          </div>
          <p className="text-emerald-200 text-sm font-medium">VNL@JFAgro Operations Platform</p>
        </div>

        <div className="relative z-10 flex-1 flex flex-col justify-center py-12">
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Sprout size={20} className="text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-base">Daily Field Logging</p>
                <p className="text-emerald-200 text-sm mt-0.5">Log pollination counts, vine care, harvest weights, and irrigation in seconds — even in poor connectivity.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <FlaskConical size={20} className="text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-base">Curing Batch Traceability</p>
                <p className="text-emerald-200 text-sm mt-0.5">Track every vanilla batch from Killing through Conditioning — moisture %, temperature, and weight loss at every stage.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Shield size={20} className="text-white" />
              </div>
              <div>
                <p className="text-white font-semibold text-base">Role-Based Access</p>
                <p className="text-emerald-200 text-sm mt-0.5">Field workers get a fast-tap station; farm managers get full analytics, leaderboards, and export.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-emerald-300 text-xs">© 2026 JFAgro Sdn. Bhd. — All rights reserved</p>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-5 py-10 lg:px-12 xl:px-16 min-h-screen lg:min-h-0">
        {/* Mobile logo */}
        <div className="flex lg:hidden items-center gap-2 mb-8">
          <AppLogo size={144} />
          <span className="font-bold text-xl text-primary-dark">VNL@JFAgro</span>
        </div>

        <div className="w-full max-w-md">
          {showForgotPassword ? (
            /* ── Forgot Password Panel ── */
            <div>
              <button
                type="button"
                onClick={handleBackToLogin}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
              >
                <ArrowLeft size={15} />
                Back to sign in
              </button>

              {resetSent ? (
                <div className="text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                    <Mail size={26} className="text-emerald-600" />
                  </div>
                  <h2 className="text-xl font-bold text-foreground mb-2">Check your email</h2>
                  <p className="text-muted-foreground text-sm mb-1">
                    We sent a password reset link to
                  </p>
                  <p className="font-semibold text-foreground text-sm mb-5">{resetEmail}</p>
                  <p className="text-muted-foreground text-xs mb-6">
                    Didn&apos;t receive it? Check your spam folder or{' '}
                    <button
                      type="button"
                      onClick={() => setResetSent(false)}
                      className="text-primary font-medium hover:underline"
                    >
                      try again
                    </button>
                    .
                  </p>
                  <button
                    type="button"
                    onClick={handleBackToLogin}
                    className="btn-primary w-full py-3 text-base"
                  >
                    Back to Sign In
                  </button>
                </div>
              ) : (
                <>
                  <div className="mb-7">
                    <h1 className="text-2xl font-bold text-foreground">Reset your password</h1>
                    <p className="text-muted-foreground text-sm mt-1">
                      Enter your account email and we&apos;ll send you a reset link.
                    </p>
                  </div>

                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        placeholder="you@jfagro.my"
                        value={resetEmail}
                        onChange={e => { setResetEmail(e.target.value); setResetEmailError(''); }}
                        className="form-input"
                        autoComplete="email"
                        autoFocus
                      />
                      {resetEmailError && (
                        <p className="form-error mt-1"><span>{resetEmailError}</span></p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isResetting}
                      className="btn-primary w-full py-3 text-base"
                    >
                      {isResetting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail size={18} />
                          Send Reset Link
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          ) : (
            /* ── Login Panel ── */
            <>
              <div className="mb-7">
                <h1 className="text-2xl font-bold text-foreground">Sign in to VNL@JFAgro</h1>
                <p className="text-muted-foreground text-sm mt-1">Enter your credentials to access your workspace</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="form-label">Username or Email</label>
                  <input
                    type="text"
                    placeholder="admin or you@jfagro.my"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                    className="form-input"
                    autoComplete="username"
                  />
                  {emailError && <p className="form-error mt-1"><span>{emailError}</span></p>}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="form-label mb-0">Password</label>
                    <button
                      type="button"
                      onClick={() => { setShowForgotPassword(true); setResetEmail(email.trim().includes('@') ? email.trim() : ''); }}
                      className="text-xs text-primary font-medium hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={password}
                      onChange={e => { setPassword(e.target.value); setPasswordError(''); }}
                      className="form-input pr-10"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {passwordError && <p className="form-error mt-1"><span>{passwordError}</span></p>}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full py-3 text-base"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      <LogIn size={18} />
                      Sign In
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}